import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Award, Lock, Sparkles, KeyRound, CheckCircle, ArrowRight, Star, Zap, Trophy, Shield, Heart, X, Download } from 'lucide-react';
import PageMeta from '../components/PageMeta';

/**
 * VaultDemo — Public demo page for school decision-makers + parents.
 * Shows the vault experience with mock data so prospects can understand
 * what students earn over 6 months.
 *
 * Sprint 230b — gallery-room backdrop + easter-egg gift orb (parent demo):
 * - When the vault door swings open the white card is replaced with a
 *   three-wall gallery scene where the 20 trophies hang as framed paintings,
 *   illustrating the moment of reveal.
 * - A subtle orb sits in the bottom-right corner of the card (identical
 *   pattern to the teen MemeGallery easter egg). On hover it glows + wobbles;
 *   on click it bursts open with a pre-generated NFT and the line
 *   "A gift from someone who loves you" — letting a parent experience first-
 *   hand what their teen receives.
 */

const DEMO_TROPHIES = [
  { title: 'First Conversation', caption: 'Started your social skills journey', category: 'social skills', date: 'Week 1' },
  { title: 'Active Listener', caption: 'Practiced empathetic listening', category: 'empathy', date: 'Week 2' },
  { title: 'New Friend Made', caption: 'Connected with a new classmate', category: 'friendship', date: 'Week 3' },
  { title: 'Team Player', caption: 'Contributed in a group activity', category: 'teamwork', date: 'Week 4' },
  { title: 'Confidence Boost', caption: 'Spoke up in class for the first time', category: 'confidence', date: 'Week 6' },
  { title: 'Kind Word', caption: 'Gave a compliment that mattered', category: 'kindness', date: 'Week 8' },
  { title: 'Conflict Resolved', caption: 'Navigated a disagreement calmly', category: 'communication', date: 'Week 10' },
  { title: 'Study Buddy', caption: 'Helped a peer with homework', category: 'teamwork', date: 'Week 12' },
  { title: 'Emotion Named', caption: 'Identified and expressed a feeling', category: 'empathy', date: 'Week 14' },
  { title: 'Boundary Set', caption: 'Said no respectfully', category: 'confidence', date: 'Week 16' },
  { title: 'Gratitude Shared', caption: 'Thanked someone meaningfully', category: 'kindness', date: 'Week 18' },
  { title: 'Public Speaker', caption: 'Presented to the class', category: 'confidence', date: 'Week 19' },
  { title: 'Deep Listener', caption: 'Asked meaningful follow-up questions', category: 'empathy', date: 'Week 20' },
  { title: 'Peacemaker', caption: 'Helped two friends reconcile', category: 'communication', date: 'Week 21' },
  { title: 'Initiative Taker', caption: 'Organised a group activity', category: 'social skills', date: 'Week 22' },
  { title: 'Empathy Champion', caption: 'Supported a friend through a hard day', category: 'empathy', date: 'Week 23' },
  { title: 'Self-Advocate', caption: 'Asked for help when needed', category: 'confidence', date: 'Week 24' },
  { title: 'Cultural Bridge', caption: 'Connected across differences', category: 'social skills', date: 'Week 25' },
  { title: 'Resilience Star', caption: 'Bounced back from a setback', category: 'confidence', date: 'Week 25' },
  { title: 'Social Intelligence Master', caption: 'Completed the full journey', category: 'milestone', date: 'Week 26' },
];

const CATEGORY_COLORS = {
  'social skills': '#7B4FD8',
  'empathy': '#2563EB',
  'friendship': '#2EAA5C',
  'teamwork': '#F5831F',
  'confidence': '#DC2626',
  'kindness': '#EC4899',
  'communication': '#0891B2',
  'milestone': '#FFD700',
};

// ── Gallery room backdrop ─────────────────────────────────────────────
// Three-wall perspective room. Trophies hang as framed paintings — back
// wall (centre row of 6), left wall (3 angled), right wall (3 angled).
// Fades in when the vault door swings open. Pure CSS, no extra deps.
const GalleryRoom = ({ visible, trophies }) => {
  const backWallTrophies = trophies.slice(0, 6);
  const leftWallTrophies = trophies.slice(6, 9);
  const rightWallTrophies = trophies.slice(9, 12);
  return (
    <div
      aria-hidden="true"
      data-testid="vault-gallery-room"
      style={{
        position: 'absolute',
        inset: 0,
        opacity: visible ? 1 : 0,
        transition: 'opacity 1.4s ease-out 0.6s',
        pointerEvents: 'none',
        overflow: 'hidden',
        borderRadius: 'inherit',
        perspective: '1100px',
        background:
          'radial-gradient(ellipse at center top, rgba(255,236,200,0.55) 0%, rgba(245,131,31,0.10) 40%, transparent 75%),' +
          'linear-gradient(180deg, #2A1B10 0%, #1A0E07 100%)',
      }}
    >
      {/* Floor */}
      <div style={{
        position: 'absolute', left: 0, right: 0, bottom: 0, height: '38%',
        transform: 'rotateX(58deg)', transformOrigin: 'top center',
        background: 'linear-gradient(180deg, #3a2a1c 0%, #1f140c 100%)',
        boxShadow: 'inset 0 40px 60px rgba(0,0,0,0.4)',
      }} />
      {/* Ceiling */}
      <div style={{
        position: 'absolute', left: 0, right: 0, top: 0, height: '12%',
        background: 'linear-gradient(180deg, #15100a 0%, #261a10 100%)',
      }} />

      {/* Back wall — 6 paintings in a row, larger */}
      <div style={{
        position: 'absolute', left: '6%', right: '6%', top: '14%',
        display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '14px',
      }}>
        {backWallTrophies.map((t, i) => <GalleryFrame key={i} trophy={t} index={i} />)}
      </div>

      {/* Left wall — 3 paintings, tilted */}
      <div style={{
        position: 'absolute', left: '2%', top: '30%', bottom: '24%',
        width: '17%',
        display: 'flex', flexDirection: 'column', justifyContent: 'space-around', gap: 14,
        transform: 'rotateY(34deg)', transformOrigin: 'right center',
      }}>
        {leftWallTrophies.map((t, i) => <GalleryFrame key={i} trophy={t} index={i + 6} tilt="left" />)}
      </div>

      {/* Right wall — 3 paintings, tilted */}
      <div style={{
        position: 'absolute', right: '2%', top: '30%', bottom: '24%',
        width: '17%',
        display: 'flex', flexDirection: 'column', justifyContent: 'space-around', gap: 14,
        transform: 'rotateY(-34deg)', transformOrigin: 'left center',
      }}>
        {rightWallTrophies.map((t, i) => <GalleryFrame key={i} trophy={t} index={i + 9} tilt="right" />)}
      </div>

      {/* Spotlight wash */}
      <div style={{
        position: 'absolute', left: '50%', top: 0, width: '60%', height: '70%',
        transform: 'translateX(-50%)',
        background: 'radial-gradient(ellipse at top, rgba(255,220,150,0.18) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />
    </div>
  );
};

// ── Painting artwork — one unique inline-SVG scene per category ────────
// Each scene is themed to the trophy's category (empathy = listening waves,
// friendship = two figures, confidence = mountain + star, etc). Driven by a
// deterministic seed (trophy index) so re-renders are stable.
const PaintingArt = ({ category, index, color }) => {
  // 7 art templates rotated by index so adjacent paintings always look
  // different even within the same category.
  const styleId = ((CATEGORY_COLORS[category] ? Object.keys(CATEGORY_COLORS).indexOf(category) : 0) + index) % 7;
  const accent = '#FFE9C2';
  const dark = '#0F0A07';

  const common = { width: '100%', height: '100%', display: 'block' };

  if (styleId === 0) {
    // Two figures — friendship / communication
    return (
      <svg viewBox="0 0 60 40" preserveAspectRatio="xMidYMid slice" style={common}>
        <rect width="60" height="40" fill={dark} />
        <circle cx="22" cy="14" r="5" fill={color} />
        <rect x="17" y="20" width="10" height="14" rx="3" fill={color} />
        <circle cx="38" cy="14" r="5" fill={accent} />
        <rect x="33" y="20" width="10" height="14" rx="3" fill={accent} />
        <path d="M27 24 Q30 22 33 24" stroke={accent} strokeWidth="1.5" fill="none" />
      </svg>
    );
  }
  if (styleId === 1) {
    // Mountain + star — confidence / resilience
    return (
      <svg viewBox="0 0 60 40" preserveAspectRatio="xMidYMid slice" style={common}>
        <rect width="60" height="40" fill={dark} />
        <polygon points="0,40 18,12 30,28 42,8 60,40" fill={color} />
        <polygon points="14,18 18,12 22,18" fill={accent} opacity="0.7" />
        <polygon points="42,8 38,15 46,15" fill={accent} opacity="0.7" />
        <polygon points="50,4 51.5,7 55,7 52,9 53,12 50,10.5 47,12 48,9 45,7 48.5,7" fill={accent} />
      </svg>
    );
  }
  if (styleId === 2) {
    // Heart — kindness / empathy
    return (
      <svg viewBox="0 0 60 40" preserveAspectRatio="xMidYMid slice" style={common}>
        <rect width="60" height="40" fill={dark} />
        <path d="M30 32 C18 22, 12 14, 20 10 C26 7, 30 14, 30 14 C30 14, 34 7, 40 10 C48 14, 42 22, 30 32 Z" fill={color} />
        <circle cx="24" cy="13" r="2" fill={accent} opacity="0.85" />
      </svg>
    );
  }
  if (styleId === 3) {
    // Speech bubbles — first conversation / active listener
    return (
      <svg viewBox="0 0 60 40" preserveAspectRatio="xMidYMid slice" style={common}>
        <rect width="60" height="40" fill={dark} />
        <rect x="6" y="8" width="22" height="14" rx="6" fill={color} />
        <polygon points="10,22 16,22 12,28" fill={color} />
        <rect x="30" y="18" width="24" height="14" rx="6" fill={accent} />
        <polygon points="48,32 42,32 46,38" fill={accent} />
        <circle cx="13" cy="15" r="1.2" fill={dark} />
        <circle cx="17" cy="15" r="1.2" fill={dark} />
        <circle cx="21" cy="15" r="1.2" fill={dark} />
      </svg>
    );
  }
  if (styleId === 4) {
    // Group / teamwork — three figures
    return (
      <svg viewBox="0 0 60 40" preserveAspectRatio="xMidYMid slice" style={common}>
        <rect width="60" height="40" fill={dark} />
        <circle cx="18" cy="15" r="4" fill={color} />
        <rect x="14" y="20" width="8" height="12" rx="2" fill={color} />
        <circle cx="30" cy="13" r="4.5" fill={accent} />
        <rect x="25" y="18" width="10" height="14" rx="2" fill={accent} />
        <circle cx="44" cy="15" r="4" fill={color} opacity="0.7" />
        <rect x="40" y="20" width="8" height="12" rx="2" fill={color} opacity="0.7" />
      </svg>
    );
  }
  if (styleId === 5) {
    // Sunrise — boundary set / self-advocate
    return (
      <svg viewBox="0 0 60 40" preserveAspectRatio="xMidYMid slice" style={common}>
        <rect width="60" height="40" fill={dark} />
        <circle cx="30" cy="38" r="20" fill={color} />
        <rect y="32" width="60" height="8" fill={dark} />
        {[6,12,18,24,30,36,42,48,54].map((x,i) => (
          <line key={i} x1={x} y1="36" x2={x} y2="40" stroke={accent} strokeWidth="0.8" opacity="0.5" />
        ))}
        <circle cx="48" cy="8" r="1.5" fill={accent} />
        <circle cx="14" cy="6" r="1" fill={accent} />
      </svg>
    );
  }
  // styleId === 6 — crown / milestone
  return (
    <svg viewBox="0 0 60 40" preserveAspectRatio="xMidYMid slice" style={common}>
      <rect width="60" height="40" fill={dark} />
      <polygon points="14,30 20,12 28,22 30,8 32,22 40,12 46,30" fill={color} />
      <rect x="14" y="30" width="32" height="4" fill={color} />
      <circle cx="20" cy="12" r="1.5" fill={accent} />
      <circle cx="30" cy="8" r="1.8" fill={accent} />
      <circle cx="40" cy="12" r="1.5" fill={accent} />
    </svg>
  );
};

const GalleryFrame = ({ trophy, index = 0, tilt }) => {
  const color = CATEGORY_COLORS[trophy?.category] || '#7B4FD8';
  return (
    <div style={{
      background: '#0F0A07',
      border: `3px solid ${color}`,
      borderRadius: 6,
      padding: 5,
      boxShadow: `0 0 0 4px #2b1d12, 0 12px 28px rgba(0,0,0,0.7), 0 0 26px ${color}40`,
    }}>
      {/* Canvas — the actual artwork inside the frame */}
      <div style={{
        position: 'relative',
        aspectRatio: '3/2',
        background: `radial-gradient(circle at 30% 25%, ${color}33, transparent 70%), #1A1209`,
        overflow: 'hidden',
        borderRadius: 2,
        boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.6)',
      }}>
        <PaintingArt category={trophy?.category} index={index} color={color} />
        {/* Gallery downlight wash on the canvas */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(180deg, rgba(255,236,200,0.18) 0%, transparent 35%)',
          pointerEvents: 'none',
        }} />
      </div>
      {/* Plaque — visible on all walls, including angled ones */}
      <p style={{
        color: '#e8d4b6', fontSize: tilt ? 8 : 10, fontWeight: 700, textAlign: 'center',
        marginTop: 4, lineHeight: 1.15, textTransform: 'uppercase', letterSpacing: '0.06em',
        textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden',
      }}>{trophy?.title}</p>
    </div>
  );
};

// ── Easter-egg gift orb + reveal modal ────────────────────────────────
const EasterEggOrb = ({ onClick }) => (
  <div
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
    className="absolute cursor-pointer group"
    style={{ bottom: 14, right: 14, zIndex: 6 }}
    data-testid="demo-easter-egg-orb"
    aria-label="A gift from someone who loves you"
  >
    <div className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"
      style={{
        background: 'radial-gradient(circle, rgba(255,200,50,0.7) 0%, rgba(255,165,0,0.35) 40%, transparent 70%)',
        transform: 'scale(2.4)', filter: 'blur(8px)', pointerEvents: 'none',
      }} />
    <img
      src="/easter-egg-nft.webp"
      alt=""
      className="relative transition-all duration-500 group-hover:scale-125"
      style={{ width: 54, height: 'auto', transform: 'rotate(-12deg)', filter: 'drop-shadow(0 2px 5px rgba(0,0,0,0.4))' }}
    />
    <style>{`
      [data-testid="demo-easter-egg-orb"] img { animation: demoEggWobble 4s ease-in-out infinite; }
      [data-testid="demo-easter-egg-orb"]:hover img { animation: demoEggWobble 1.2s ease-in-out infinite; }
      @keyframes demoEggWobble {
        0%,100% { transform: rotate(-12deg) translateY(0); }
        50%     { transform: rotate(-6deg)  translateY(-3px); }
      }
    `}</style>
  </div>
);

const DemoGiftReveal = ({ onClose }) => {
  const [phase, setPhase] = useState('burst');
  useEffect(() => {
    const t1 = setTimeout(() => setPhase('reveal'), 700);
    const t2 = setTimeout(() => setPhase('shown'), 1800);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  const glitter = useRef(
    Array.from({ length: 50 }).map((_, i) => ({
      left: Math.random() * 100,
      delay: Math.random() * 2.5,
      duration: 2 + Math.random() * 2.5,
      size: 3 + Math.random() * 5,
      color: ['#FFD700', '#FF8C00', '#FF69B4', '#7B4FD8', '#2EAA5C', '#2563EB', '#F5831F', '#EC4899'][i % 8],
    }))
  ).current;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" data-testid="demo-gift-reveal">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {glitter.map((p, i) => (
          <div key={i} className="absolute rounded-full" style={{
            left: `${p.left}%`, top: '-10px', width: `${p.size}px`, height: `${p.size}px`,
            backgroundColor: p.color,
            animation: `demoGiftFall ${p.duration}s linear ${p.delay}s infinite`,
          }} />
        ))}
        <style>{`@keyframes demoGiftFall { 0%{transform:translateY(0) rotate(0)} 100%{transform:translateY(110vh) rotate(720deg)} }`}</style>
      </div>

      <div
        className="relative bg-white rounded-3xl shadow-2xl overflow-hidden max-w-md w-[92%] mx-auto"
        style={{
          transform: phase === 'burst' ? 'scale(0.4)' : 'scale(1)',
          opacity:   phase === 'burst' ? 0 : 1,
          transition: 'transform 0.6s cubic-bezier(0.34,1.56,0.64,1), opacity 0.4s ease-out',
        }}
        data-testid="demo-gift-card"
      >
        <button onClick={onClose}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/10 hover:bg-black/20 flex items-center justify-center z-10"
          data-testid="demo-gift-close"
          aria-label="Close gift reveal"
        ><X size={18} /></button>

        <div style={{
          background: 'linear-gradient(135deg, #F5831F 0%, #EC4899 60%, #7B4FD8 100%)',
          padding: '24px 24px 20px',
          color: 'white',
          textAlign: 'center',
        }}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-2"
            style={{ backgroundColor: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(4px)' }}>
            <Heart size={14} fill="currentColor" />
            <span className="text-xs font-bold tracking-wider uppercase">A gift from someone who loves you</span>
          </div>
          <h2 className="text-2xl font-bold mt-1" data-testid="demo-gift-title">You&apos;re not alone on this journey.</h2>
          <p className="text-sm opacity-95 mt-1">This is exactly what your teen sees when their first surprise collectible drops.</p>
        </div>

        <div className="p-6 text-center">
          <div className="relative mx-auto mb-4" style={{ width: 200, height: 200 }}>
            <div className="absolute inset-0 rounded-2xl"
              style={{
                background: 'radial-gradient(circle, rgba(255,200,50,0.5) 0%, transparent 70%)',
                animation: 'demoGiftGlow 2.4s ease-in-out infinite',
              }} />
            <img src="/easter-egg-nft.webp" alt="Pre-generated HiBFF collectible NFT" className="relative w-full h-full object-contain"
              data-testid="demo-gift-image" />
            <style>{`@keyframes demoGiftGlow { 0%,100%{opacity:0.5;transform:scale(0.95)} 50%{opacity:0.95;transform:scale(1.1)} }`}</style>
          </div>
          <p className="text-base font-bold" style={{ color: '#1A1209' }}>HiBFF Founding Collectible</p>
          <p className="text-xs mt-1" style={{ color: '#7A6A5A' }}>
            Unique to your teen · 1 of 1 · Minted on Crossmint
          </p>

          <div className="mt-4 rounded-xl p-3 text-left" style={{ backgroundColor: '#FFF7ED', border: '1px solid #FED7AA' }}>
            <p className="text-xs leading-relaxed" style={{ color: '#7C2D12' }}>
              <strong>What your teen reads on their screen:</strong> <em>&ldquo;Someone who loves you wanted you to have this on day one. Keep going — they&rsquo;re proud of you already.&rdquo;</em>
            </p>
          </div>

          <button onClick={onClose}
            className="mt-5 w-full py-3 rounded-xl font-bold text-white text-sm"
            style={{ background: 'linear-gradient(135deg, #F5831F 0%, #EC4899 100%)' }}
            data-testid="demo-gift-dismiss"
          >Beautiful — close demo</button>
        </div>
      </div>
    </div>
  );
};

const DemoVaultDoor = ({ phase, dialRotation }) => {
  const isOpen = phase === 'open';
  const showCode = phase === 'code';

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: 460,
        aspectRatio: '1/1',
        margin: '0 auto',
        // The slide track: door translates leftward out of the frame.
        // We overflow:visible so the door clears the steel card to the left.
        overflow: 'visible',
      }}
    >
      {/* Photorealistic vault door image — same asset as the teen vault */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          transition: 'transform 2.2s cubic-bezier(0.65, 0, 0.35, 1)',
          transform: isOpen ? 'translateX(-118%)' : 'translateX(0)',
          filter: isOpen ? 'drop-shadow(-20px 10px 30px rgba(0,0,0,0.6))' : 'drop-shadow(0 12px 24px rgba(0,0,0,0.4))',
          zIndex: 3,
        }}
        data-testid="demo-vault-door-photo"
      >
        <img
          src="/vault-door.png"
          alt="HiBFF Vault"
          className="w-full h-full object-cover rounded-xl"
          draggable={false}
        />
        {/* Hinge edge — only visible while the door is sliding open */}
        <div style={{
          position: 'absolute', top: 0, bottom: 0, right: -6, width: 12,
          background: 'linear-gradient(90deg, rgba(0,0,0,0.5), rgba(0,0,0,0))',
          opacity: isOpen ? 1 : 0,
          transition: 'opacity 0.6s ease-out 0.6s',
          borderRadius: '0 6px 6px 0',
        }} />

        {/* Centre overlay — countdown / status pip, sits on the door face */}
        <div
          style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <div style={{
            width: 110, height: 110, borderRadius: '50%',
            background: 'radial-gradient(circle at 30% 30%, rgba(0,0,0,0.55), rgba(0,0,0,0.85))',
            border: `2px solid ${showCode ? '#F5831F' : isOpen ? '#2EAA5C' : '#7B4FD8'}`,
            boxShadow: `0 0 30px ${showCode ? 'rgba(245,131,31,0.6)' : isOpen ? 'rgba(46,170,92,0.6)' : 'rgba(123,79,216,0.6)'}`,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            transform: `rotate(${dialRotation}deg)`, transition: 'transform 0.5s cubic-bezier(0.34,1.56,0.64,1), border-color 0.4s, box-shadow 0.4s',
          }}>
            {isOpen ? (
              <CheckCircle size={36} style={{ color: '#2EAA5C', transform: `rotate(-${dialRotation}deg)` }} />
            ) : showCode ? (
              <>
                <KeyRound size={22} style={{ color: '#F5831F', transform: `rotate(-${dialRotation}deg)` }} />
                <p style={{ color: '#F5831F', fontSize: 9, fontWeight: 800, letterSpacing: '0.1em', marginTop: 3, transform: `rotate(-${dialRotation}deg)` }}>ENTERING</p>
              </>
            ) : (
              <>
                <Lock size={22} style={{ color: '#7B4FD8' }} />
                <p style={{ color: '#7B4FD8', fontSize: 9, fontWeight: 800, letterSpacing: '0.1em', marginTop: 3 }}>LOCKED</p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Door-frame edge that stays put on the right when the door slides left */}
      <div style={{
        position: 'absolute', top: 6, bottom: 6, right: -3, width: 6, borderRadius: 3,
        background: 'linear-gradient(180deg, #4a4a4a 0%, #1f1f1f 100%)',
        boxShadow: 'inset 0 0 2px rgba(255,255,255,0.2)',
        opacity: isOpen ? 1 : 0,
        transition: 'opacity 0.6s ease-out 0.4s',
        zIndex: 2,
      }} />
    </div>
  );
};

const VaultDemo = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('locked'); // locked → code → open
  const [revealedCount, setRevealedCount] = useState(0);
  const [dialRotation, setDialRotation] = useState(0);
  const [demoCode, setDemoCode] = useState('');
  const [showGiftReveal, setShowGiftReveal] = useState(false);
  const timelineRef = useRef(null);

  // Auto-advance the demo
  useEffect(() => {
    if (phase === 'locked') {
      const t = setTimeout(() => setPhase('code'), 3000);
      return () => clearTimeout(t);
    }
    if (phase === 'code') {
      // Simulate code entry
      const codeDigits = '847291';
      let i = 0;
      const t = setInterval(() => {
        if (i < 6) {
          setDemoCode(codeDigits.slice(0, i + 1));
          setDialRotation((i + 1) * 60);
          i++;
        } else {
          clearInterval(t);
          setTimeout(() => setPhase('open'), 600);
        }
      }, 400);
      return () => clearInterval(t);
    }
    if (phase === 'open') {
      // Reveal trophies one by one
      let i = 0;
      const t = setInterval(() => {
        if (i < 20) {
          setRevealedCount(i + 1);
          i++;
        } else {
          clearInterval(t);
        }
      }, 150);
      return () => clearInterval(t);
    }
  }, [phase]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: embedded ? 'transparent' : '#FAFAF8' }} data-testid="vault-demo-page">
      <PageMeta title="Vault Demo" path="/vault-demo" noIndex />
      {/* Nav — suppressed when embedded inside the parent shell */}
      {!embedded && (
        <nav className="bg-white border-b border-gray-100 px-4 py-3">
          <div className="max-w-5xl mx-auto flex items-center justify-between">
            <img src="/hibff-logo.png" alt="HiBFF" className="h-10 cursor-pointer" onClick={() => navigate('/')} />
            <div className="flex items-center gap-3">
              <span className="text-xs font-medium px-3 py-1 rounded-full" style={{ backgroundColor: '#7B4FD815', color: '#7B4FD8' }}>Interactive Demo</span>
              <button onClick={() => navigate('/school/register')} className="px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: '#F5831F' }} data-testid="demo-register-btn">
                Register Your School
              </button>
            </div>
          </div>
        </nav>
      )}

      {/* Hero */}
      <div className="max-w-5xl mx-auto px-4 pt-12 pb-8 text-center">
        <h1 className="text-3xl sm:text-4xl font-bold mb-3" style={{ color: '#1A1209' }} data-testid="demo-heading">
          The Vault: What Your Students Earn
        </h1>
        <p className="text-base sm:text-lg max-w-2xl mx-auto" style={{ color: '#666' }}>
          Over 6 months, students collect 20 achievement trophies by completing real-world social challenges. Watch the vault unlock.
        </p>
      </div>

      {/* Vault Animation — outer card styled as brushed steel "bank vault frame" */}
      <div className="max-w-5xl mx-auto px-4">
        <div
          className="relative rounded-2xl p-8 mb-8 text-center"
          style={{
            // Brushed steel gradient matching the door's metallic feel
            background: phase === 'open'
              ? 'linear-gradient(135deg, #4d5258 0%, #2e3338 35%, #3a4046 65%, #1f2226 100%)'
              : 'linear-gradient(135deg, #c8ccd0 0%, #9ca1a8 28%, #d8dce0 55%, #8d9298 80%, #b5babf 100%)',
            border: '1px solid #4a5158',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35), inset 0 -1px 0 rgba(0,0,0,0.3), 0 18px 40px -16px rgba(0,0,0,0.45)',
            transition: 'background 0.8s ease-out',
            minHeight: 520,
            // Allow the door image to slide outside the card to the left
            overflow: 'visible',
            position: 'relative',
          }}
        >
          {/* Brushed-steel rivets in each corner — bolted-on industrial detail */}
          {[
            { top: 10, left: 10 }, { top: 10, right: 10 },
            { bottom: 10, left: 10 }, { bottom: 10, right: 10 },
          ].map((p, i) => (
            <div key={i} style={{
              position: 'absolute', width: 10, height: 10, borderRadius: '50%',
              background: 'radial-gradient(circle at 30% 30%, #d8dce0, #4a5158)',
              boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.5), 0 1px 2px rgba(0,0,0,0.5)',
              ...p,
            }} />
          ))}

          {/* Inner inset frame — clips the gallery room cleanly inside the steel border */}
          <div
            className="relative rounded-xl"
            style={{
              overflow: 'hidden',
              minHeight: 460,
              background: phase === 'open' ? 'transparent' : 'rgba(0,0,0,0.05)',
              boxShadow: 'inset 0 4px 12px rgba(0,0,0,0.3)',
            }}
          >
            {/* Gallery room — fades in only when the door is open */}
            <GalleryRoom visible={phase === 'open'} trophies={DEMO_TROPHIES} />

            {/* Easter-egg gift orb — bottom-right, like the teen surface */}
            <EasterEggOrb onClick={() => setShowGiftReveal(true)} />

            <div className="relative" style={{ zIndex: 2, padding: '24px 12px 18px' }}>
              <div className="flex justify-center mb-6">
                <DemoVaultDoor phase={phase} dialRotation={dialRotation} />
              </div>

              {/* Phase status text */}
              <div className="mb-6">
                {phase === 'locked' && (
                  <div className="animate-pulse">
                    <p className="text-sm font-medium" style={{ color: '#1A1209' }}>
                      <Lock size={14} className="inline mr-1" /> Vault is locked — student collects trophies over 6 months...
                    </p>
                  </div>
                )}
                {phase === 'code' && (
                  <div>
                    <p className="text-sm font-medium mb-3" style={{ color: '#F5831F' }}>
                      <KeyRound size={14} className="inline mr-1" /> 6 months reached! Entering combination...
                    </p>
                    <div className="flex justify-center gap-2">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="w-10 h-12 rounded-lg border-2 flex items-center justify-center text-xl font-bold transition-all"
                          style={{
                            borderColor: i < demoCode.length ? '#F5831F' : '#9CA1A8',
                            backgroundColor: i < demoCode.length ? '#FFF7ED' : 'rgba(255,255,255,0.7)',
                            color: '#1A1209',
                          }}>
                          {demoCode[i] || ''}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {phase === 'open' && (
                  <p className="text-sm font-semibold" style={{ color: '#86EFAC' }}>
                    <CheckCircle size={14} className="inline mr-1" /> Vault unlocked! Step inside the gallery...
                  </p>
                )}
              </div>

              {/* Progress bar */}
              <div className="max-w-md mx-auto">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium" style={{ color: phase === 'open' ? '#FAF5EE' : '#1A1209' }}>Trophy Collection</span>
                  <span className="text-xs font-bold" style={{ color: revealedCount >= 20 ? '#2EAA5C' : phase === 'open' ? '#FFD700' : '#7B4FD8' }}>{revealedCount}/20</span>
                </div>
                <div className="w-full h-2.5 rounded-full" style={{ backgroundColor: phase === 'open' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)' }}>
                  <div className="h-full rounded-full transition-all duration-300" style={{
                    width: `${(revealedCount / 20) * 100}%`,
                    background: revealedCount >= 20 ? 'linear-gradient(90deg, #2EAA5C, #34D399)' : 'linear-gradient(90deg, #7B4FD8, #9B7FE8)',
                  }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trophy Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3 mb-10" ref={timelineRef} data-testid="demo-trophy-grid">
          {DEMO_TROPHIES.map((trophy, i) => {
            const revealed = i < revealedCount;
            const color = CATEGORY_COLORS[trophy.category] || '#7B4FD8';
            return (
              <div
                key={i}
                className="bg-white rounded-xl p-4 transition-all duration-500"
                style={{
                  border: `1px solid ${revealed ? color + '30' : '#E5E7EB'}`,
                  opacity: revealed ? 1 : 0.3,
                  transform: revealed ? 'scale(1)' : 'scale(0.92)',
                  filter: revealed ? 'none' : 'grayscale(1)',
                }}
                data-testid={`demo-trophy-${i}`}
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center mb-2" style={{ backgroundColor: color + '15' }}>
                  {trophy.category === 'milestone' ? <Star size={18} style={{ color }} /> : <Award size={18} style={{ color }} />}
                </div>
                <h4 className="text-xs font-bold mb-0.5 truncate" style={{ color: revealed ? '#1A1209' : '#AAA' }}>{trophy.title}</h4>
                <p className="text-[10px] truncate" style={{ color: revealed ? '#666' : '#CCC' }}>{trophy.caption}</p>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full capitalize" style={{ backgroundColor: color + '10', color }}>{trophy.category}</span>
                  <span className="text-[9px]" style={{ color: '#999' }}>{trophy.date}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* How It Works */}
        <div className="bg-white rounded-2xl p-8 mb-8" style={{ border: '1px solid #E5E7EB' }}>
          <h2 className="text-2xl font-bold text-center mb-6" style={{ color: '#1A1209' }}>How The Vault Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { icon: Sparkles, title: 'Challenges', desc: 'Students complete real-world social challenges with their AvA coach', color: '#7B4FD8' },
              { icon: Trophy, title: 'Trophies', desc: 'Each challenge earns a unique AI-generated achievement trophy', color: '#F5831F' },
              { icon: Lock, title: 'Vault', desc: 'After 6 months, a secret combination unlocks the vault', color: '#1A1209' },
              { icon: Zap, title: 'NFTs', desc: 'Trophies become tradeable NFTs — real digital ownership', color: '#2EAA5C' },
            ].map((step, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: step.color + '10' }}>
                  <step.icon size={24} style={{ color: step.color }} />
                </div>
                <h3 className="text-sm font-bold mb-1" style={{ color: '#1A1209' }}>{step.title}</h3>
                <p className="text-xs" style={{ color: '#666' }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Safety note */}
        <div className="flex items-center gap-3 p-4 rounded-xl mb-8" style={{ backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE' }}>
          <Shield size={20} style={{ color: '#2563EB' }} />
          <p className="text-xs" style={{ color: '#2563EB' }}>
            <strong>Built for safety.</strong> All interactions are monitored by our patent-pending Empathetic AI system. Conversations are never shared. COPPA and SOC 2 compliant.
          </p>
        </div>

        {/* CTA — suppressed when embedded in the parent shell */}
        {!embedded && (
          <div className="text-center pb-16">
            <h2 className="text-2xl font-bold mb-3" style={{ color: '#1A1209' }}>Ready to bring HiBFF to your school?</h2>
            <p className="text-sm mb-6" style={{ color: '#666' }}>90-day free beta. No credit card required. Full pastoral dashboard included.</p>
            <div className="flex items-center justify-center gap-4">
              <button onClick={() => navigate('/school/register')} className="flex items-center gap-2 px-8 py-3 rounded-xl text-white text-sm font-semibold" style={{ backgroundColor: '#F5831F' }} data-testid="demo-cta-register">
                Register Your School <ArrowRight size={16} />
              </button>
              <button onClick={() => navigate('/')} className="px-6 py-3 rounded-xl text-sm font-medium border" style={{ borderColor: '#E5E7EB', color: '#666' }}>
                Learn More
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Easter-egg gift reveal — fires when parent clicks the bottom-right orb */}
      {showGiftReveal && <DemoGiftReveal onClose={() => setShowGiftReveal(false)} />}
    </div>
  );
};

export default VaultDemo;
